import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type User, useLocalePreference, useSaveTheme, useSaveUiScale } from '@/entities/viewer';
import { isLocale, SUPPORTED_LOCALES } from '@/shared/i18n';
import { FormError } from '@/shared/forms';
import {
  applyScale,
  normalizeScale,
  UI_SCALE_DEFAULT,
  UI_SCALE_MAX,
  UI_SCALE_MIN,
  UI_SCALE_STEP,
} from '@/shared/lib/ui-scale';
import { applyTheme, THEME_PREFERENCES, type ThemePreference } from '@/shared/lib/theme';
import { Button } from '@/shared/ui';

import styles from './InterfaceSettings.module.css';
import { ScalePreview } from './ScalePreview';

const SAVE_DELAY_MS = 400;

/**
 * Display settings of the interface. Today one setting - the scale - lives here; further
 * preferences are added as more `<section>`s below, each saved through the same account
 * endpoint.
 *
 * The slider applies at once (CSS custom properties, the page and the preview react while
 * dragging) and is saved shortly after the last change. A failed save puts the saved value back.
 */
export function InterfaceSettings({ user }: { user: User }) {
  const { t, i18n } = useTranslation();
  const chooseLocale = useLocalePreference();
  const update = useSaveUiScale();
  const saveTheme = useSaveTheme();
  const [theme, setTheme] = useState<ThemePreference>(user.theme);
  const [themeError, setThemeError] = useState(false);
  const [value, setValue] = useState(user.ui_scale);
  const [error, setError] = useState<unknown>(null);
  const timer = useRef<number | null>(null);
  const saved = useRef(user.ui_scale);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const save = (next: number) => {
    update.mutate(next, {
      onSuccess: () => {
        saved.current = next;
        setError(null);
      },
      onError: (reason) => {
        setError(reason);
        setValue(saved.current);
        applyScale(saved.current); // the server refused: back to what is saved
      },
    });
  };

  const change = (raw: number) => {
    const next = normalizeScale(raw);
    setValue(next);
    applyScale(next); // immediate
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      if (next !== saved.current) save(next);
    }, SAVE_DELAY_MS);
  };

  const reset = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    setValue(UI_SCALE_DEFAULT);
    applyScale(UI_SCALE_DEFAULT);
    if (saved.current !== UI_SCALE_DEFAULT) save(UI_SCALE_DEFAULT);
  };

  const chooseTheme = (next: ThemePreference) => {
    const before = theme;
    setTheme(next);
    setThemeError(false);
    applyTheme(next); // immediate
    saveTheme.mutate(next, {
      onError: () => {
        setTheme(before);
        applyTheme(before);
        setThemeError(true);
      },
    });
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('interfaceSettings.title')}</h1>

      <section className={styles.card} aria-labelledby="language-title">
        <h2 id="language-title" className={styles.sectionTitle}>
          {t('interfaceSettings.language.title')}
        </h2>
        <p className={styles.note}>{t('interfaceSettings.language.hint')}</p>
        <label className={styles.languageField}>
          <span className="visually-hidden">{t('locale.label')}</span>
          <select
            value={i18n.resolvedLanguage}
            onChange={(event) => {
              if (isLocale(event.target.value)) chooseLocale(event.target.value);
            }}
          >
            {SUPPORTED_LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {t(`locale.${locale}`)}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className={styles.card} aria-labelledby="theme-title">
        <h2 id="theme-title" className={styles.sectionTitle}>
          {t('interfaceSettings.theme.title')}
        </h2>
        <p className={styles.note}>{t('interfaceSettings.theme.hint')}</p>
        <div className={styles.themeChoices} role="radiogroup" aria-labelledby="theme-title">
          {THEME_PREFERENCES.map((value) => (
            <label
              key={value}
              className={`${styles.themeChoice} ${theme === value ? styles.themeOn : ''}`}
            >
              <input
                type="radio"
                name="theme"
                value={value}
                checked={theme === value}
                onChange={() => {
                  chooseTheme(value);
                }}
              />
              <span>{t(`interfaceSettings.theme.${value}`)}</span>
            </label>
          ))}
        </div>
        {themeError && (
          <p className={styles.error} role="alert">
            {t('interfaceSettings.theme.saveFailed')}
          </p>
        )}
      </section>

      <section className={styles.card} aria-labelledby="scale-title">
        <h2 id="scale-title" className={styles.sectionTitle}>
          {t('interfaceSettings.scale')}
        </h2>
        <p className={styles.note}>{t('interfaceSettings.scaleHint')}</p>
        <div className={styles.control}>
          <input
            type="range"
            className={styles.slider}
            aria-label={t('interfaceSettings.scale')}
            aria-valuetext={`${String(value)}%`}
            min={UI_SCALE_MIN}
            max={UI_SCALE_MAX}
            step={UI_SCALE_STEP}
            value={value}
            onChange={(event) => {
              change(Number(event.target.value));
            }}
          />
          <output className={styles.value} data-testid="scale-value" aria-live="polite">
            {value}%
          </output>
          <Button disabled={value === UI_SCALE_DEFAULT} onClick={reset}>
            {t('interfaceSettings.reset')}
          </Button>
        </div>
        <FormError error={error} />
        <p className={styles.note} role="status">
          {update.isPending ? t('interfaceSettings.saving') : ''}
        </p>
        <h3 className={styles.previewTitle}>{t('interfaceSettings.preview')}</h3>
        <ScalePreview />
      </section>
    </div>
  );
}
